import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark multifamily community',
    },
  },
};

export function InvestorsHero() {
  const bg = useSlotGroup('investorshero.background', bgDef);

  return (
    <section className="relative flex min-h-[min(70svh,684px)] md:min-h-[684px] w-full flex-col justify-end overflow-clip bg-[rgb(var(--ala-dark))]">
      <EditableSlotGroup groupPath="investorshero.background" definition={bgDef}>
        <div className="absolute inset-0 z-0">
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(91.65deg, rgb(var(--ala-primary)) 0%, rgb(var(--ala-primary)/0) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 flex flex-col gap-4 p-6 md:pb-[clamp(22px,6vw,87px)] md:pl-[clamp(16px,6vw,87px)]"
      >
        <div className="flex items-center gap-3">
          <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
          <RichTextContent
            path="investorshero.eyebrow"
            label="Eyebrow"
            as="span"
            className="font-heading text-base font-bold uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
          />
        </div>
        <RichTextContent
          path="investorshero.title"
          label="Title"
          as="h1"
          className="font-heading text-[clamp(38px,4.86vw,70px)] font-bold leading-[1.15] text-[rgb(var(--ala-on-primary))]"
        />
      </motion.div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InvestorsHero',
  displayName: 'Investors Hero',
  tokens: {
    'investorshero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Investors</p>',
    },
    'investorshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Investors &amp; Capital Markets</p>',
    },
  },
  slotGroups: {
    'investorshero.background': bgDef,
  },
};

(InvestorsHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InvestorsHero;
